/**
 * Shabbat & Jewish Holiday storefront mode — Hebcal (Jerusalem, Israel).
 * Fail-open: if the API is unavailable, the store stays open.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'hebcal_shabbat_holiday_mode_v1';
  const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
  const RANGE_PAST_DAYS = 1;
  const RANGE_FUTURE_DAYS = 21;
  const API_BASE = 'https://www.hebcal.com/hebcal/';

  const config = window.ShabbatHolidayModeConfig || {};

  function formatDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function jerusalemToday() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const map = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
    return new Date(`${map.year}-${map.month}-${map.day}T12:00:00`);
  }

  function addDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  function buildApiUrl(startDate, endDate) {
    const params = new URLSearchParams({
      v: '1',
      cfg: 'json',
      start: formatDate(startDate),
      end: formatDate(endDate),
      c: 'on',
      geo: 'city',
      city: 'Jerusalem',
      Israel: '1',
      maj: 'on',
      min: 'on',
      mod: 'on',
      nx: 'on',
    });
    return `${API_BASE}?${params.toString()}`;
  }

  function buildClosedPeriods(items) {
    if (!Array.isArray(items)) return [];

    const timed = items
      .filter((item) => item.category === 'candles' || item.category === 'havdalah')
      .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

    const periods = [];
    let pendingStart = null;

    timed.forEach((item) => {
      if (item.category === 'candles') {
        pendingStart = item.date;
        return;
      }

      if (item.category === 'havdalah' && pendingStart) {
        periods.push({
          start: pendingStart,
          end: item.date,
        });
        pendingStart = null;
      }
    });

    return periods;
  }

  function isWithinClosedPeriod(periods, timestamp) {
    return periods.some((period) => {
      const start = Date.parse(period.start);
      const end = Date.parse(period.end);
      return timestamp >= start && timestamp < end;
    });
  }

  function readCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.periods)) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      /* ignore quota / private mode */
    }
  }

  function isCacheValid(cache) {
    if (!cache || !cache.fetchedAt) return false;
    return Date.now() - cache.fetchedAt < CACHE_TTL_MS;
  }

  function shouldRefreshCache(cache) {
    if (!isCacheValid(cache)) return true;
    const now = Date.now();
    const nearRangeEnd = cache.rangeEnd && now > Date.parse(`${cache.rangeEnd}T23:59:59+03:00`) - 2 * 24 * 60 * 60 * 1000;
    return nearRangeEnd;
  }

  async function fetchClosedPeriods() {
    const today = jerusalemToday();
    const startDate = addDays(today, -RANGE_PAST_DAYS);
    const endDate = addDays(today, RANGE_FUTURE_DAYS);
    const response = await fetch(buildApiUrl(startDate, endDate), {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error(`Hebcal API error: ${response.status}`);
    }

    const payload = await response.json();
    const periods = buildClosedPeriods(payload.items);

    return {
      fetchedAt: Date.now(),
      expiresAt: Date.now() + CACHE_TTL_MS,
      rangeStart: formatDate(startDate),
      rangeEnd: formatDate(endDate),
      periods,
    };
  }

  function setActive(active) {
    const screen = document.getElementById('shabbat-holiday-mode-screen');
    document.documentElement.classList.toggle('shabbat-holiday-mode-active', active);

    if (!screen) return;

    if (active) {
      screen.removeAttribute('hidden');
      screen.setAttribute('aria-hidden', 'false');
    } else {
      screen.setAttribute('hidden', '');
      screen.setAttribute('aria-hidden', 'true');
    }
  }

  function evaluateAndApply(periods) {
    const closed = isWithinClosedPeriod(periods, Date.now());
    setActive(closed);
    return closed;
  }

  function scheduleNextCheck(periods) {
    const now = Date.now();
    const upcoming = [];

    periods.forEach((period) => {
      const start = Date.parse(period.start);
      const end = Date.parse(period.end);
      if (start > now) upcoming.push(start);
      if (end > now) upcoming.push(end);
    });

    if (!upcoming.length) return;

    const nextAt = Math.min(...upcoming) - now + 1000;
    const delay = Math.max(1000, Math.min(nextAt, 60 * 60 * 1000));

    window.setTimeout(() => {
      ShabbatHolidayMode.refresh();
    }, delay);
  }

  async function refresh() {
    if (config.designMode || !config.enabled) {
      setActive(Boolean(config.preview));
      return { closed: Boolean(config.preview), source: 'disabled' };
    }

    if (config.preview) {
      setActive(true);
      return { closed: true, source: 'preview' };
    }

    let cache = readCache();
    let periods = cache && cache.periods ? cache.periods : [];

    if (shouldRefreshCache(cache)) {
      try {
        cache = await fetchClosedPeriods();
        writeCache(cache);
        periods = cache.periods;
      } catch (error) {
        if (!periods.length) {
          setActive(false);
          return { closed: false, source: 'api-error' };
        }
      }
    }

    const closed = evaluateAndApply(periods);
    scheduleNextCheck(periods);
    return { closed, source: 'hebcal' };
  }

  window.ShabbatHolidayMode = {
    refresh,
    isWithinClosedPeriod,
    buildClosedPeriods,
    readCache,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
